import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DirectionalHint, TooltipDelay } from "@fluentui/react";
import { MEASURE_TOOLS } from "../../../../constants/Constants";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import themeContext from "../../../../context/theme/ThemeContext";
import GetToolSVG from "../Tools/ToolComponents/GetToolSVG";
import DeleteIcon from "../../../../components/SVGComponents/DeleteIcon";
import MeasureAngle from "../../../../components/SVGComponents/Tools/MeasureAngle";
import DropDown from "../../../../components/SVGComponents/Tools/DropDown";
import CustomToolTipHost from "../../../../components/CustomToolTipHost/CustomToolTipHost";
import ToolbarMoreMenuRow from "./ToolbarMoreMenuRow";
import { getBasicViewerToolShortcutLabel } from "../../helpers/basicViewerToolShortcuts";
import { toolBarFlex, toolTipStyles } from "../../styles/styles";

/**
 * Desktop "Measure" dropdown
 */
const MeasureDropDown = () => {
  const { t } = useTranslation();
  const { theme } = useContext(themeContext);
  const { activeModality } = useContext(ThumbnailContext);
  const {
    activeTool,
    setActiveTool,
    setPreviousActiveTool,
    isMPRActive,
    activeMPRTool,
    setActiveMPRTool,
    setPreviousActiveMPRTool,
    removeSelectedMeasurement,
    removeAllMeasurements,
    handleDefaultToolChange,
  } = useContext(ViewportContext);

  const [showMeasure, setShowMeasure] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!showMeasure) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowMeasure(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showMeasure]);

  const currentActive = isMPRActive ? activeMPRTool : activeTool;
  const isMeasureActive =
    showMeasure || MEASURE_TOOLS.some((tl) => tl.key === currentActive);

  // Toggle behaviour identical to MobileMeasureSheet.handleSelect: re-clicking the
  // already-active measure tool reverts to the default tool;
  const handleSelect = (tool) => {
    const isAlreadyActive = currentActive === tool.key;
    if (isAlreadyActive) {
      if (isMPRActive) {
        setPreviousActiveMPRTool(activeMPRTool);
      } else {
        setPreviousActiveTool(activeTool);
      }
      handleDefaultToolChange();
      setShowMeasure(false);
      return;
    }
    if (isMPRActive) {
      setPreviousActiveMPRTool(activeMPRTool);
      setActiveMPRTool(tool.key);
    } else {
      setPreviousActiveTool(activeTool);
      setActiveTool(tool.key);
    }
    setShowMeasure(false);
  };

  return (
    <div ref={containerRef} style={{ position: "relative", ...toolBarFlex }}>
      <CustomToolTipHost
        id="measure-tool"
        content={t("viewer.tools.measure", "Measure")}
        delay={TooltipDelay.zero}
        directionalHint={DirectionalHint.bottomCenter}
        styles={toolTipStyles}
        className="myToolTip"
      >
        <div
          className="highlight-tool"
          onClick={() => setShowMeasure((prev) => !prev)}
          aria-describedby="measure-tool"
          style={{ width: "55px", gap: "4px", paddingLeft: "0px", paddingRight: "0px", cursor: "pointer" }}
        >
          <MeasureAngle
            color={isMeasureActive ? theme.palette.activeTool : theme.palette.inactiveTool}
          />
          <DropDown
            color={isMeasureActive ? theme.palette.activeTool : theme.palette.inactiveTool}
          />
        </div>
      </CustomToolTipHost>

      {showMeasure && (
        <div
          className="viewer-tool-more-dropdown viewer-toolbar-more-menu viewer-tool-more-dropdown-height"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            borderRadius: "5px",
            boxShadow:
              "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
          }}
        >
          {MEASURE_TOOLS.map((tool, index) => {
            if (tool.NotFor.includes(activeModality)) return null;
            const isActive = currentActive === tool.key;
            return (
              <ToolbarMoreMenuRow
                key={index}
                label={t(tool.name)}
                shortcut={getBasicViewerToolShortcutLabel(tool.key)}
                onClick={() => handleSelect(tool)}
              >
                <div className="highlight-tool">
                  {/* `hovered` doubles as the active-highlight flag for the icon */}
                  <GetToolSVG toolName={tool.key} hovered={isActive} />
                </div>
              </ToolbarMoreMenuRow>
            );
          })}

          {/* Delete Selected / Delete All */}
          <div style={{ borderTop: "1px solid #3B3A39", margin: "4px 0" }} />

          <ToolbarMoreMenuRow
            label={t("viewer.tools.delete-selected-measurement", "Delete Selected")}
            shortcut="SHIFT + D, DELETE"
            onClick={() => {
              removeSelectedMeasurement();
              setShowMeasure(false);
            }}
          >
            <div className="highlight-tool">
              <DeleteIcon color={theme.palette.inactiveTool} />
            </div>
          </ToolbarMoreMenuRow>

          <ToolbarMoreMenuRow
            label={t("viewer.tools.delete-all-measurements", "Delete All")}
            shortcut="CTRL/CMD + DELETE"
            onClick={() => {
              removeAllMeasurements();
              setShowMeasure(false);
            }}
          >
            <div className="highlight-tool">
              <DeleteIcon color={theme.palette.inactiveTool} />
            </div>
          </ToolbarMoreMenuRow>
        </div>
      )}
    </div>
  );
};

export default MeasureDropDown;